// Permanent ability progression. Levels are cumulative and never removed by death.

import type {
  Ability,
  AbilityId,
  AbilityLevel,
  AbilityLevelDefinition,
  LocationId,
  LoopState,
  PermanentState,
  TrustRequirement,
} from "../types"
import { getAbilityById, getFactionById } from "./content"
import { isTrustAtLeast } from "./factionEngine"

const LEVEL_ORDER: AbilityLevel[] = ["none", "beginner", "advanced", "master"]

export function getAbilityLevel(
  permanent: PermanentState,
  abilityId: AbilityId,
): AbilityLevel {
  return permanent.abilities[abilityId] ?? "none"
}

export function isAtLeastLevel(
  current: AbilityLevel,
  required: AbilityLevel,
): boolean {
  return LEVEL_ORDER.indexOf(current) >= LEVEL_ORDER.indexOf(required)
}

/**
 * Requirement strings follow the `${abilityId}_${level}` convention used in
 * abilities.json (e.g. "swordsmanship_beginner"). Anything else is treated
 * as a knowledge flag.
 */
export function meetsRequirement(
  permanent: PermanentState,
  requirement: string,
): boolean {
  const matchedLevel = LEVEL_ORDER.slice(1).find((level) =>
    requirement.endsWith(`_${level}`),
  )
  if (!matchedLevel) {
    return permanent.knowledge.includes(requirement)
  }
  const abilityId = requirement.slice(
    0,
    requirement.length - matchedLevel.length - 1,
  )
  return isAtLeastLevel(getAbilityLevel(permanent, abilityId), matchedLevel)
}

/** A level's teacher requirement is satisfied when it needs no fixed location, or the player is there. */
function isAtRequiredLocation(
  levelDefinition: AbilityLevelDefinition,
  currentLocationId: LocationId,
): boolean {
  return (
    levelDefinition.locationId === null ||
    levelDefinition.locationId === currentLocationId
  )
}

/**
 * Whether the teacher's faction trusts the player enough to teach them.
 * Unlike ability requirements this is loop state, so a master level behind a
 * trust gate must be re-unlocked every loop even though the level itself,
 * once earned, is permanent.
 */
function meetsTrustRequirement(
  loop: LoopState,
  levelDefinition: AbilityLevelDefinition,
): boolean {
  const gate = levelDefinition.trustRequirement
  if (!gate) return true
  return isTrustAtLeast(loop, gate.factionId, gate.level)
}

/**
 * Whether the teacher for this level is still alive to give it. Loop state,
 * like the trust gate above: the anti-player killing a swordmaster costs the
 * player the rest of that loop's lessons, not the levels they already hold.
 */
function isTeacherAvailable(
  loop: LoopState,
  levelDefinition: AbilityLevelDefinition,
): boolean {
  return !(levelDefinition.blockedByFlags ?? []).some((flag) =>
    loop.worldFlags.includes(flag),
  )
}

/** Renders a trust gate as readable text, e.g. "High Elves (recognized)". */
export function describeTrustRequirement(gate: TrustRequirement): string {
  return `${getFactionById(gate.factionId)?.name ?? gate.factionId} (${gate.level})`
}

export function canTrain(
  permanent: PermanentState,
  abilityId: AbilityId,
  level: Exclude<AbilityLevel, "none">,
  currentLocationId: LocationId,
  loop: LoopState,
): boolean {
  const ability = getAbilityById(abilityId)
  const levelDefinition = ability?.levels.find((l) => l.level === level)
  if (!levelDefinition) return false
  if (!isAtRequiredLocation(levelDefinition, currentLocationId)) return false
  if (!meetsTrustRequirement(loop, levelDefinition)) return false
  if (!isTeacherAvailable(loop, levelDefinition)) return false
  return levelDefinition.requirements.every((req) =>
    meetsRequirement(permanent, req),
  )
}

/** The next level of this ability the player is currently eligible to train here, if any. */
export function getNextTrainableLevel(
  permanent: PermanentState,
  abilityId: AbilityId,
  currentLocationId: LocationId,
  loop: LoopState,
): AbilityLevelDefinition | undefined {
  const ability = getAbilityById(abilityId)
  if (!ability) return undefined
  const currentLevel = getAbilityLevel(permanent, abilityId)
  return ability.levels.find(
    (levelDefinition) =>
      !isAtLeastLevel(currentLevel, levelDefinition.level) &&
      canTrain(permanent, abilityId, levelDefinition.level, currentLocationId, loop),
  )
}

export interface UpcomingAbilityLevel {
  ability: Ability
  levelDefinition: AbilityLevelDefinition
  /** Whether this level's teacher (if any) is at the given location. */
  atRequiredLocation: boolean
  /** Requirement strings (e.g. "swordsmanship_beginner") not yet satisfied. */
  missingRequirements: string[]
  /** The teacher's faction-trust gate, when the player has not yet earned it this loop. */
  missingTrust: TrustRequirement | null
  /** True when the teacher was killed this loop, so no amount of preparation unlocks it. */
  teacherLost: boolean
  trainable: boolean
}

/**
 * The next not-yet-reached level of an ability, regardless of whether it can
 * currently be trained — so the UI can show *why* it's locked (per Phase 3's
 * "see unavailable options with their requirements") rather than simply
 * hiding it. Returns undefined once the ability has reached master.
 */
export function getUpcomingLevel(
  permanent: PermanentState,
  abilityId: AbilityId,
  currentLocationId: LocationId,
  loop: LoopState,
): UpcomingAbilityLevel | undefined {
  const ability = getAbilityById(abilityId)
  if (!ability) return undefined
  const currentLevel = getAbilityLevel(permanent, abilityId)
  const levelDefinition = ability.levels.find(
    (l) => !isAtLeastLevel(currentLevel, l.level),
  )
  if (!levelDefinition) return undefined
  const atRequiredLocation = isAtRequiredLocation(levelDefinition, currentLocationId)
  const missingRequirements = levelDefinition.requirements.filter(
    (req) => !meetsRequirement(permanent, req),
  )
  const missingTrust = meetsTrustRequirement(loop, levelDefinition)
    ? null
    : levelDefinition.trustRequirement ?? null
  const teacherLost = !isTeacherAvailable(loop, levelDefinition)
  return {
    ability,
    levelDefinition,
    atRequiredLocation,
    missingRequirements,
    missingTrust,
    teacherLost,
    trainable:
      atRequiredLocation &&
      missingRequirements.length === 0 &&
      missingTrust === null &&
      !teacherLost,
  }
}

/** Renders a requirement string (e.g. "swordsmanship_beginner") as readable text for locked-option displays. */
export function describeRequirement(requirement: string): string {
  const matchedLevel = LEVEL_ORDER.slice(1).find((level) =>
    requirement.endsWith(`_${level}`),
  )
  if (!matchedLevel) return requirement
  const abilityId = requirement.slice(0, requirement.length - matchedLevel.length - 1)
  const ability = getAbilityById(abilityId)
  return `${ability?.name ?? abilityId} (${matchedLevel})`
}

export function trainAbility(
  permanent: PermanentState,
  abilityId: AbilityId,
  level: Exclude<AbilityLevel, "none">,
  currentLocationId: LocationId,
  loop: LoopState,
): PermanentState {
  if (!canTrain(permanent, abilityId, level, currentLocationId, loop)) {
    throw new Error(
      `Requirements not met to train ${abilityId} to ${level}`,
    )
  }
  return {
    ...permanent,
    abilities: { ...permanent.abilities, [abilityId]: level },
  }
}
